import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase.config';

/**
 * Contact Matcher Utility
 * Matches phone numbers and names with synced contacts for party auto-fill
 */

/**
 * Normalize phone number for comparison
 * Removes spaces, dashes, country codes, etc.
 */
export const normalizePhone = (phone) => {
  if (!phone) return '';
  
  // Remove all non-digit characters
  let normalized = phone.replace(/\D/g, '');
  
  // Remove country code (91 for India)
  if (normalized.startsWith('91') && normalized.length === 12) {
    normalized = normalized.substring(2);
  }
  
  // Get last 10 digits
  if (normalized.length > 10) {
    normalized = normalized.slice(-10);
  }
  
  return normalized;
};

/**
 * Search synced contacts by phone number
 * Returns array of matching contacts
 */
export const searchContactByPhone = async (phoneNumber, userId, appId) => {
  try {
    const normalizedInput = normalizePhone(phoneNumber);
    
    if (normalizedInput.length < 10) {
      return []; // Need at least 10 digits
    }
    
    // Get all synced contacts for this user
    const contactsRef = collection(db, `apps/${appId}/synced_contacts`);
    const q = query(
      contactsRef,
      where('userId', '==', userId)
    );
    
    const snapshot = await getDocs(q);
    const matches = [];
    
    snapshot.forEach(doc => {
      const contact = doc.data();
      
      // Check each phone number in the contact
      const hasMatch = contact.phones?.some(phone => {
        const contactPhone = normalizePhone(phone.number);
        return contactPhone === normalizedInput;
      });
      
      if (hasMatch) {
        matches.push({
          id: doc.id,
          ...contact,
          matchedOn: 'phone',
          confidence: 95
        });
      }
    });
    
    return matches;
  } catch (error) {
    console.error('Error searching contacts by phone:', error);
    return [];
  }
};

/**
 * Search synced contacts by name
 * Returns array of matching contacts
 */
export const searchContactByName = async (name, userId, appId) => {
  try {
    if (!name || name.length < 3) {
      return []; // Need at least 3 characters
    }
    
    const contactsRef = collection(db, `apps/${appId}/synced_contacts`);
    const q = query(
      contactsRef,
      where('userId', '==', userId)
    );
    
    const snapshot = await getDocs(q);
    const matches = [];
    const searchLower = name.toLowerCase();
    
    snapshot.forEach(doc => {
      const contact = doc.data();
      const contactNameLower = contact.name?.toLowerCase() || '';
      
      // Fuzzy match: check if search term is in contact name
      if (contactNameLower.includes(searchLower)) {
        const relevance = calculateRelevance(searchLower, contactNameLower);
        matches.push({
          id: doc.id,
          ...contact,
          matchedOn: 'name',
          relevance,
          confidence: relevance
        });
      }
    });
    
    // Sort by relevance (exact match first)
    matches.sort((a, b) => b.relevance - a.relevance);
    
    return matches;
  } catch (error) {
    console.error('Error searching contacts by name:', error);
    return [];
  }
};

/**
 * Calculate relevance score for name matching
 */
const calculateRelevance = (search, contactName) => {
  // Exact match: 100
  if (contactName === search) return 100;
  
  // Starts with search: 80
  if (contactName.startsWith(search)) return 80;
  
  // Contains search: 50
  if (contactName.includes(search)) return 50;
  
  // Contains words from search: 30
  const searchWords = search.split(' ');
  const hasAllWords = searchWords.every(word => contactName.includes(word));
  if (hasAllWords) return 30;
  
  return 10;
};

/**
 * Get best contact match with confidence score
 */
export const getBestContactMatch = async (partyData, userId, appId) => {
  let bestMatch = null;
  let confidence = 0;
  
  // Try phone match first (highest confidence)
  if (partyData.phone) {
    const phoneMatches = await searchContactByPhone(partyData.phone, userId, appId);
    if (phoneMatches.length > 0) {
      bestMatch = phoneMatches[0];
      confidence = 95; // High confidence for phone match
    }
  }
  
  // Try name match if no phone match
  if (!bestMatch && partyData.name) {
    const nameMatches = await searchContactByName(partyData.name, userId, appId);
    if (nameMatches.length > 0 && nameMatches[0].relevance > 80) {
      bestMatch = nameMatches[0];
      confidence = nameMatches[0].relevance;
    }
  }
  
  return {
    match: bestMatch,
    confidence,
    suggestion: bestMatch ? generateSuggestionText(bestMatch, confidence) : null
  };
};

/**
 * Generate user-friendly suggestion text
 */
const generateSuggestionText = (contact, confidence) => {
  if (confidence >= 90) {
    return `Found exact match: ${contact.name}`;
  } else if (confidence >= 70) {
    return `Found likely match: ${contact.name}`;
  } else {
    return `Found possible match: ${contact.name}`;
  }
};

/**
 * Check for duplicate parties by phone
 */
export const checkDuplicateParty = async (phone, userId, appId, currentPartyId = null) => {
  try {
    const normalizedPhone = normalizePhone(phone);
    if (normalizedPhone.length < 10) return null;
    
    // Check synced contacts
    const contactMatches = await searchContactByPhone(phone, userId, appId);
    
    // TODO: Also check existing parties in database
    // This would require access to the parties collection
    
    return {
      hasDuplicates: contactMatches.length > 0,
      contacts: contactMatches,
      warning: contactMatches.length > 0 
        ? `Found ${contactMatches.length} contact(s) with this phone number` 
        : null
    };
  } catch (error) {
    console.error('Error checking duplicates:', error);
    return null;
  }
};











